import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle,
  Clock,
  MessageSquare
} from 'lucide-react';
import PublicHeader from './components/PublicHeader';
import PublicFooter from './components/PublicFooter';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContactPage = () => {
  const [content, setContent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service_interest: '',
    subject: '',
    message: '',
    website_url: ''  // honeypot - must stay empty
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const services = [
    'Brand Strategy',
    'Digital Marketing',
    'Web Design & Development',
    'Content Marketing',
    'SEO & Paid Search',
    'Social Media Marketing',
    'Packaging Design',
    'Trade Show & Events',
    'Other'
  ];

  // Default content
  const defaults = {
    badge_text: "Let's Connect",
    page_title: 'Get in Touch',
    page_subtitle: "Ready to elevate your marketing? Let's discuss how we can help you sharpen your message, strengthen your brand, and achieve real results.",
    location: 'Southport, NC',
    email: 'info@sptdigitalmarketing.com',
    phone: '',
    response_time: 'Within 24-48 hours',
    form_title: 'Send Us a Message',
    success_title: 'Thank You for Reaching Out!',
    success_message: "We've received your message and will get back to you within 24-48 hours. In the meantime, feel free to explore our work and insights.",
    faqs: [
      { question: 'What types of businesses do you work with?', answer: 'We work with businesses of all sizes across various industries, with particular expertise in home improvement, building products, manufacturing, and professional services.' },
      { question: 'How do you typically start a new project?', answer: 'Every engagement begins with a discovery conversation to understand your goals, challenges, and market position. From there, we develop a tailored strategy and clear scope of work.' },
      { question: 'What makes SPT Digital Marketing different?', answer: 'We combine 40+ years of enterprise-level marketing experience with the personal attention of a boutique agency. You get strategic depth and creative excellence with direct access to senior leadership.' },
      { question: 'What are your typical project timelines?', answer: 'Timelines vary based on project scope. Brand strategy projects typically take 6-12 weeks, websites 8-16 weeks, and ongoing marketing engagements are structured around monthly or quarterly cycles.' }
    ]
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/public/site-content/contact`);
        if (response.data && Object.keys(response.data).length > 0) {
          setContent(response.data);
        }
      } catch (err) {
        console.log('Using default content');
      }
    };
    fetchContent();
  }, []);

  // Merge CMS content with defaults
  const c = { ...defaults, ...content };
  const faqs = (content?.faqs && content.faqs.length > 0) ? content.faqs : defaults.faqs;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/public/contact`, formData);
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } catch (err) {
      // For demo, still show success
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        
        <section className="pt-40 pb-20 lg:pt-48 lg:pb-32 bg-gradient-to-br from-slate-50 via-white to-blue-50">
          <div className="max-w-2xl mx-auto px-6 lg:px-8 text-center">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-8">
              <CheckCircle className="w-10 h-10" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--color-heading-text, #0F172A)' }}>
              {c.success_title}
            </h1>
            <p className="text-xl mb-8" style={{ color: 'var(--color-body-text, #475569)' }}>
              {c.success_message}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/portfolio">
                <Button variant="outline" size="lg">View Our Portfolio</Button>
              </Link>
              <Link to="/blog">
                <Button 
                  size="lg"
                  style={{ 
                    backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
                    color: 'var(--color-button-text, #FFFFFF)',
                    borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                  }}
                >Read Our Insights</Button>
              </Link>
            </div>
          </div>
        </section>

        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero Section */}
      <section 
        className="pt-40 pb-16 lg:pt-48 lg:pb-20"
        style={{ background: `linear-gradient(to bottom right, var(--color-hero-bg-from, #F8FAFC), white, var(--color-hero-bg-to, #EFF6FF))` }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
            >
              <MessageSquare className="w-4 h-4" />
              {c.badge_text}
            </div>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ color: 'var(--color-heading-text, #0F172A)' }}
            >
              {c.page_title}
            </h1>
            <p 
              className="text-xl leading-relaxed"
              style={{ color: 'var(--color-body-text, #475569)' }}
            >
              {c.page_subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 
                  className="text-2xl font-bold mb-6"
                  style={{ color: 'var(--color-heading-text, #0F172A)' }}
                >Contact Information</h2>
                <p style={{ color: 'var(--color-body-text, #475569)' }} className="mb-8">
                  Reach out through the form or contact us directly. We typically respond 
                  within 24-48 hours.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="border-0 shadow-md" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
                  <CardContent className="p-6 flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                    >
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Location</h3>
                      <p style={{ color: 'var(--color-body-text, #475569)' }}>{c.location}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-md" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
                  <CardContent className="p-6 flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                    >
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Email</h3>
                      <a 
                        href={`mailto:${c.email}`} 
                        className="hover:underline"
                        style={{ color: 'var(--color-primary, #1F3C4F)' }}
                      >
                        {c.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                {c.phone && (
                  <Card className="border-0 shadow-md" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
                    <CardContent className="p-6 flex items-start gap-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                      >
                        <Phone className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Phone</h3>
                        <a 
                          href={`tel:${c.phone}`} 
                          className="hover:underline"
                          style={{ color: 'var(--color-primary, #1F3C4F)' }}
                        >
                          {c.phone}
                        </a>
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="border-0 shadow-md" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
                  <CardContent className="p-6 flex items-start gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: 'var(--color-primary, #1F3C4F)1a', color: 'var(--color-primary, #1F3C4F)' }}
                    >
                      <Clock className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1" style={{ color: 'var(--color-heading-text, #0F172A)' }}>Response Time</h3>
                      <p style={{ color: 'var(--color-body-text, #475569)' }}>{c.response_time}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-xl" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
                <CardContent className="p-8 lg:p-12">
                  <h2 
                    className="text-2xl font-bold mb-8"
                    style={{ color: 'var(--color-heading-text, #0F172A)' }}
                  >{c.form_title}</h2>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot field - hidden from real users, bots fill it */}
                    <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
                      <label htmlFor="website_url">Leave blank</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex={-1}
                        autoComplete="off"
                        value={formData.website_url}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                          data-testid="contact-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          required
                          data-testid="contact-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="(555) 123-4567"
                          data-testid="contact-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company">Company</Label>
                        <Input
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Your company name"
                          data-testid="contact-company"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="service_interest">Service Interest</Label>
                      <Select 
                        value={formData.service_interest}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, service_interest: value }))}
                      >
                        <SelectTrigger data-testid="contact-service">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service} value={service}>
                              {service}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="How can we help?"
                        data-testid="contact-subject"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your project or how we can help..."
                        rows={6}
                        required
                        data-testid="contact-message"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      size="lg" 
                      className="w-full"
                      disabled={loading}
                      data-testid="contact-submit"
                      style={{ 
                        backgroundColor: 'var(--color-button-bg, var(--color-primary, #1F3C4F))',
                        color: 'var(--color-button-text, #FFFFFF)',
                        borderRadius: 'var(--btn-computed-radius, 0.5rem)'
                      }}
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        className="py-16 lg:py-24"
        style={{ backgroundColor: 'var(--color-section-alt-bg, #F8FAFC)' }}
      >
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 
            className="text-3xl font-bold mb-12 text-center"
            style={{ color: 'var(--color-heading-text, #0F172A)' }}
          >
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {faqs.map((faq, idx) => (
              <Card key={idx} className="border-0 shadow-md" style={{ backgroundColor: 'var(--color-card-bg, #FFFFFF)' }}>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2" style={{ color: 'var(--color-heading-text, #0F172A)' }}>
                    {faq.question}
                  </h3>
                  <p style={{ color: 'var(--color-body-text, #475569)' }}>
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ContactPage;
